import {
    Box,
    Button,
    Chip,
    OutlinedInput,
    Switch,
    Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import classes from "./WorkbenchUpload.module.scss";
import { exportWorkbenchFile, exportSchemaToWorkbenchFile } from "store";
import { useDispatch, useSelector } from "react-redux";
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PreviewIcon from '@mui/icons-material/Preview';

const WorkbenchUploadBox = React.memo(({ onCancel }) => {

    const { loadingexportWorkbenchFileApi, loadingExportSchemaToWorkbenchFileApi } = useSelector(
        (state) => state.aggregator
    );

    const dispatch = useDispatch();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [dataMap, setDataMap] = useState(null);
    const [outputSchemaMap, setOutputSchemaMap] = useState(null);

    //File Upload and Output File Name
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const metaFileRef = useRef(null);
    const [outputFileName, setOutputFileName] = useState('');
    const [open, setOpen] = useState(false);
    
    const [checked, setChecked] = useState(false);
    const handleSwitchChange = () => {
        setChecked(!checked);
    };

    const handleFileNameChange = (e) => {
        setOutputFileName(e.target.value)
    }

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const handleUpload = async () => {
        if (!file) {
            setError('No file selected');
            return;
        }
        try {
            dispatch(exportWorkbenchFile({ file: file, outputFileName: outputFileName, isMeta: false, isExistingTable: !checked })).unwrap()
                .then((data) => {
                    setDataMap(data?.dataTypeMap);
                    handleReset();
                    setActiveStep((prev) => prev + 1);
                })
                .catch((err) => {
                    setError(err.message);
                });
        } catch (error) {
            setError(error.message);
        } finally {
        }
    };

    const handleMetaUpload = async () => {

        if (!file) {
            setError('No file selected');
            return;
        }
        try {
            dispatch(exportWorkbenchFile({ file: file, outputFileName: outputFileName, isMeta: true, columnFileMap: dataMap })).unwrap()
                .then((data) => {
                    setOutputSchemaMap(data.outputSchema)
                    setError('');
                    setActiveStep((prev) => prev + 1);
                })
                .catch((err) => {
                    setError(err.message);
                });
        } catch (error) {
            setError(error.message);
        } finally {
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
            setFileName(selectedFile.name);
            setError('');
        } else {
            setError('Please select a valid CSV file.');
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(outputSchemaMap);
        } catch (err) {

        }
    };

    const onDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const files = e.dataTransfer?.files;
        if (!files || files.length === 0) return;

        const fileList = files;
        try {
            const dt = new DataTransfer();
            for (let i = 0; i < fileList.length; i++) dt.items.add(fileList[i]);
            if (fileRef.current) {
                fileRef.current.files = dt.files;
                fileRef.current.dispatchEvent(new Event('change', { bubbles: true }));
            }
        } catch (err) {

            handleFileChange?.({ target: { files: fileList } });
            return;
        }
        handleFileChange?.({ target: { files: fileList } });
    };


    const onMetaDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const files = e.dataTransfer?.files;
        if (!files || files.length === 0) return;

        const fileList = files;
        try {
            const dt = new DataTransfer();
            for (let i = 0; i < fileList.length; i++) dt.items.add(fileList[i]);
            if (metaFileRef.current) {
                metaFileRef.current.files = dt.files;
                metaFileRef.current.dispatchEvent(new Event('change', { bubbles: true }));
            }
        } catch (err) {

            handleFileChange?.({ target: { files: fileList } });
            return;
        }
        handleFileChange?.({ target: { files: fileList } });
    };

    const handleReset = () => {
        setError('');
        setFile(null);
        setFileName('');
        fileRef.current && (fileRef.current.value = '');
        metaFileRef.current && (metaFileRef.current.value = '');
    };

    const uploadToWorkbench = (e) => {
        try {
            dispatch(exportSchemaToWorkbenchFile({ outputSchema: outputSchemaMap, outputFileName: outputFileName, isExistingTable: !checked })).unwrap()
                .then((data) => {
                    setError('');
                    setActiveStep((prev) => prev + 1);
                })
                .catch((err) => {
                    setError(err.message);
                });
        } catch (error) {
            setError(error.message);
        } finally {
        }
        setSnackbarOpen(true);
        setActiveStep(prev => prev + 1);
    }

    const handleNext = async () => {
        if (activeStep === 0) {
            if (!file) {
                setError("Please select a CSV file..");
                return;
            }

            if (!validateFileName(outputFileName).valid) {
                setError(validateFileName(outputFileName).error);
                return;
            }

            try {
                await handleUpload();
            } catch (err) {
                setError(err?.message || "Upload failed");
            }
        }

        if (activeStep === 1) {
            if (!file) {
                setError("Please select a CSV file..");
                return;
            }

            try {
                await handleMetaUpload();
            } catch (err) {
                setError(err?.message || "Upload failed");
            }
        }

        if (activeStep === 2) {

            try {
                await uploadToWorkbench();
            } catch (err) {
                setError(err?.message || "Upload failed");
            }

        }
    };


    function Modal({ isOpen, onClose, title, children }) {
        if (!isOpen) return null;
        return (
            <div className={classes.overlay} onMouseDown={onClose}>
                <div className={classes.modal} onMouseDown={e => e.stopPropagation()}>
                    <div className={classes.header} >
                        <Typography variant="subtitle2">{title || 'JSON Viewer'}</Typography>
                        <button onClick={onClose} className={classes.close}>✕</button>
                    </div>
                    <div className={classes.body} >{children}</div>
                </div>
            </div>
        );
    }

    const steps = [
        {
            label: 'Upload Workbench File',
            description: (
                <><section className={classes.section}>
                    <section className={classes.header}>
                        <Typography className={classes.label}>Select a file below to begin uploading data</Typography>
                    </section>
                    <section className={classes.formSection}>
                        <div className={classes.formContent}>
                            <label for="images" className={classes.dropContainer} id="dropcontainer"
                                onDragEnter={onDragEnter}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}>
                                <span className={classes.dropTitle}>Drop files here</span>
                                or
                                <div className={classes.inputContainer}>
                                    <input ref={fileRef} type="file" accept=".csv" onChange={handleFileChange} />
                                </div>
                            </label>
                            {fileName && <p>Selected file: <strong>{fileName}</strong></p>}
                            {fileName && <Chip label={fileName} onDelete={handleReset} onClick={() => { }} />}
                        </div>
                    </section>
                    <section className={classes.header}>
                        <Typography className={classes.label}>Enter Output File Name</Typography>
                    </section>
                    <section className={classes.section}>
                        <section className={classes.formSection}>
                            <OutlinedInput
                                placeholder="Output File Name"
                                value={outputFileName}
                                onChange={handleFileNameChange}
                                className={classes.inputBoxStyle}
                            />
                        </section>
                    </section>
                    <section className={classes.header}>
                        <Typography className={classes.label}>Would you like to create a new table or update the existing one?</Typography>
                    </section>
                    <section className={classes.section}>
                        <section className={classes.switchSection}>
                            Overwrite <Switch
                                checked={checked} onChange={handleSwitchChange} color={checked ? "primary" : "default"}
                            /> Create New
                        </section>
                    </section>
                </section></>
            ),
        },
        {
            label: 'Upload Metafile',
            description: (
                <><section className={classes.section}>
                    <section className={classes.header}>
                        <Typography className={classes.label}>Select a file below to begin uploading data</Typography>
                        <Typography className={classes.highlightLabel} variant="caption">*Please provide metadata file with "column name" and "description" columns.</Typography>
                    </section>
                    <section className={classes.formSection}>
                        <div className={classes.formContent}>
                            {/* <h3>Upload Metafile</h3> */}
                            <label for="images" className={classes.dropContainer} id="dropcontainer"
                                onDragEnter={onDragEnter}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onMetaDrop}>
                                <span className={classes.dropTitle}>Drop files here</span>
                                or
                                <div className={classes.inputContainer}>
                                    <input ref={metaFileRef} type="file" accept=".csv" onChange={handleFileChange} />
                                </div>
                            </label>
                            {fileName && <p>Selected file: <strong>{fileName}</strong></p>}
                            {fileName && <Chip label={fileName} onDelete={handleReset} onClick={() => { }} />}
                        </div>
                    </section>
                </section></>
            ),
        },
        {
            label: 'Upload to Workbench',
            description: (<section className={classes.section}>
                <section className={classes.header}>
                    <Typography className={classes.label}>Please verify the table schema below or copy it.</Typography>
                </section>
                <section className={classes.previewSection}>
                    <IconButton aria-label="view" onClick={() => setOpen(true)} className={classes.previewButton}>
                        <PreviewIcon />
                        <Typography>View Table</Typography>
                    </IconButton>
                    <IconButton aria-label="copy" onClick={handleCopy} className={classes.previewButton}>
                        <ContentCopyIcon />
                        <Typography>Copy Table</Typography>
                    </IconButton>
                </section>
                <section className={classes.formSection}>
                    <p>Please click on the Push to Workbench button below.</p>
                </section>
            </section>),
        },
    ];

    const validateFileName = (fileName) => {
        if (typeof fileName !== 'string') {
            return { valid: false, error: 'Filename must be a string.' };
        }

        if (fileName.length === 0) {
            return { valid: false, error: 'Filename is required.' };
        }

        if (/^\s+$/.test(fileName)) {
            return { valid: false, error: 'Filename cannot be only whitespace.' };
        }

        if (/^[\d\s]/.test(fileName)) {
            return { valid: false, error: 'Filename cannot start with a number or a space.' };
        }

        // eslint-disable-next-line no-useless-escape
        const invalidCharsRegex = /[!@#$%^&*()+=\[\]{};':"\\|,<>\/?~`]/;
        if (invalidCharsRegex.test(fileName)) {
            return { valid: false, error: 'Filename contains invalid characters.' };
        }

        if (fileName.length > 50) {
            return { valid: false, error: 'Filename is too long (max 50 characters).' };
        }

        return { valid: true, error: null };
    }

    function Node({ name, value }) {
        const [open, setOpen] = useState(true);
        const isObject = value && typeof value === 'object' && !Array.isArray(value);
        const isArray = Array.isArray(value);

        return (
            <div style={{ marginLeft: 12, fontFamily: 'monospace' }}>
                <div onClick={() => setOpen(!open)} style={{ cursor: isObject || isArray ? ',pointer' : 'default' }}>
                    {(isObject || isArray) ? (open ? '▾ ' : '▸ ') : '  '}
                    {name ? <strong>{name}: </strong> : null}
                    {isObject ? '{...}' : isArray ? `Array(${value.length})` : String(value)}
                </div>
                {open && (isObject || isArray) && (
                    <div>
                        {Object.entries(value).map(([k, v]) => (
                            <Node key={k} name={k} value={v} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <Box className={classes.body}>
            <section className={classes.sectionHeader} >
                <IconButton onClick={onCancel} color="primary">
                    <CloseIcon />
                </IconButton>
            </section>
            <Box>
                <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((step, index) => (
                        <Step key={step.label}>
                            <StepLabel
                                optional={
                                    index === steps.length - 1 ? (
                                        <Typography variant="caption">Last step</Typography>
                                    ) : null
                                }
                            >
                                {step.label}
                            </StepLabel>
                            <StepContent>
                                <Typography>{step.description}</Typography>
                                <Box sx={{ mb: 2 }}>

                                    {index === 0 && <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                        {loadingexportWorkbenchFileApi ? 'Uploading....' : 'Upload'}
                                    </Button>}

                                    {index === 1 && <><Button
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                        {loadingExportSchemaToWorkbenchFileApi ? 'Uploading....' : 'Upload'}
                                    </Button>
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                setActiveStep((prev) => prev - 1);
                                            }}
                                            sx={{ mt: 1, mr: 1 }}
                                        >
                                            Back
                                        </Button></>}

                                    {index === 2 && <><Button
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                        Push To Workbench
                                    </Button><Button
                                        variant="contained"
                                        onClick={() => {
                                            setActiveStep((prev) => prev - 1);
                                        }}
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                            Back
                                        </Button></>}

                                    {error !== '' && <MuiAlert className={classes.muiAlert} severity="error">
                                        {error}
                                    </MuiAlert>}
                                </Box>
                            </StepContent>
                        </Step>
                    ))}
                </Stepper>
                {activeStep === steps.length && (
                    <Paper square elevation={0} sx={{ p: 3 }}>
                        <Typography>All steps are complete — your schema will be updated to DynamoDB and Database soon.</Typography>
                        <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                            Reset & Upload New File
                        </Button>
                        <Snackbar
                            open={snackbarOpen}
                            autoHideDuration={5000}
                            onClose={handleSnackbarClose}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MuiAlert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                                <Typography color="success.main" sx={{ mt: 1 }}>Table Schema Uploaded Successfully</Typography>
                            </MuiAlert>
                        </Snackbar>
                    </Paper>
                )}
                <Modal isOpen={open} onClose={() => setOpen(false)} title={`Table Schema - ${outputFileName}`}>
                    <Node name="" value={JSON.parse(outputSchemaMap)} />
                </Modal>
            </Box>
        </Box>
    );
});

export default WorkbenchUploadBox;
